/**
 * Session statistics service for In-Session Mode
 * Tracks and manages session stats during gameplay
 */

import { db } from '../firebase';
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  runTransaction,
  increment,
  arrayUnion,
  deleteField,
} from 'firebase/firestore';
import { SessionStats, SessionSummary } from '../types/inSessionStats';
import { debug, debugError } from './inSessionDebug';
import { stripUndefinedDeep } from './firestoreSanitize';
import { applyParticipationStreakAward, breakParticipationStreakMessage } from './participationStreak';
import { evaluateFlowStateAfterSuccess, mergeFlowClearIntoRow } from './liveEventFlowState';
import {
  applyFlowPpRewardMultiplier,
  flowStateToFirestore,
  getNewlyReachedBoonThresholds,
  parseFlowStateFromPlayerRow,
} from './liveEventFlowBoons';
import type { LiveEventPowerGain } from '../types/playerPowerStats';
import {
  awardPowerXpForElimination,
  computeSessionEndPowerXp,
  liveEventPowerGainHasPositiveAmount,
  applySessionEndBattlePassAndPowerToStudent,
} from './liveEventPowerStatsService';
import { unlockLevel2BuilderFromLiveFlow } from '../services/level2ManifestService';
import { trackPlayerAction } from './playerProgressionRewards';
import { trackDailyChallengeProgress } from './liveEventDailyChallengeTracking';
import { inferEnergyTypeForLiveEvent } from '../constants/energyTypes';
import { buildSessionActivitySummary } from './liveEventSessionActivitySummary';
import { skillUseDebug, truncateId } from './liveEventDebugLogging';
import { computeSiegeProtectionFloor } from './liveEventGameTimeService';

/** Base PP awarded per elimination in a live event (eliminator also receives the eliminated player's vault PP) */
export const LIVE_EVENT_PP_BASE_PER_ELIMINATION = 500;
/** PP awarded per participation point in a live event */
export const LIVE_EVENT_PP_PER_PARTICIPATION_POINT = 50;

/** Eliminated players keep this fraction of quiz + elimination PP (participation PP in stats is not deducted from vault). */
export const LIVE_EVENT_ELIMINATED_PP_FRACTION = 0.5;

/**
 * Initialize session stats for a player when they join
 */
export async function initializePlayerStats(
  sessionId: string,
  playerId: string,
  playerName: string,
  startingPP: number
): Promise<boolean> {
  try {
    const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
    
    const initialStats: SessionStats = {
      playerId,
      playerName,
      startingPP,
      endingPP: startingPP,
      netPPGained: 0,
      ppSpent: 0,
      ppEarned: 0,
      vaultPpGrantedMidSession: 0,
      participationEarned: 0,
      movesEarned: 0,
      eliminations: 0,
      isEliminated: false,
      damageDealt: 0,
      damageTaken: 0,
      healingGiven: 0,
      healingReceived: 0,
      skillsUsed: [],
      totalSkillsUsed: 0,
      sessionId,
      sessionStartTime: serverTimestamp(),
      sessionEndTime: null,
      sessionDuration: 0
    };
    
    await setDoc(statsRef, initialStats);
    
    debug('inSessionStats', `Initialized stats for player ${playerId} in session ${sessionId}`);
    return true;
  } catch (error) {
    debugError('inSessionStats', `Error initializing stats for player ${playerId}`, error);
    return false;
  }
}

/**
 * Ensures stats/{playerId} exists. Use when join used the "already in session" fast path,
 * failed-precondition recovery, or stats init failed — otherwise trackSkillUsage no-ops and attacks don't register.
 */
export async function ensurePlayerStatsIfMissing(
  sessionId: string,
  playerId: string,
  defaults?: { playerName?: string; startingPP?: number }
): Promise<boolean> {
  try {
    const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
    const snap = await getDoc(statsRef);
    if (snap.exists()) return true;

    let playerName = defaults?.playerName ?? 'Player';
    let startingPP = defaults?.startingPP ?? 0;
    const roomRef = doc(db, 'inSessionRooms', sessionId);
    const roomSnap = await getDoc(roomRef);
    if (roomSnap.exists()) {
      const players = (roomSnap.data() as { players?: Array<{ userId: string; displayName?: string; powerPoints?: number }> }).players ?? [];
      const row = players.find((p) => p.userId === playerId);
      if (row) {
        if (row.displayName) playerName = row.displayName;
        if (typeof row.powerPoints === 'number') startingPP = row.powerPoints;
      }
    }

    const ok = await initializePlayerStats(sessionId, playerId, playerName, startingPP);
    if (ok) debug('inSessionStats', `ensurePlayerStatsIfMissing: created stats for ${playerId}`);
    return ok;
  } catch (error) {
    debugError('inSessionStats', `ensurePlayerStatsIfMissing failed for ${playerId}`, error);
    return false;
  }
}

/**
 * Track a skill usage
 */
export async function trackSkillUsage(
  sessionId: string,
  playerId: string,
  skillId: string,
  skillName: string,
  ppCost: number,
  damage?: number,
  healing?: number,
  energyType?: string
): Promise<boolean> {
  try {
    await ensurePlayerStatsIfMissing(sessionId, playerId, {});
    const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
    
    await runTransaction(db, async (transaction) => {
      const statsDoc = await transaction.get(statsRef);
      
      if (!statsDoc.exists()) {
        debug('inSessionStats', `Stats still missing for ${playerId} after ensure — skip skill tracking`);
        return;
      }
      
      const stats = statsDoc.data() as SessionStats;
      
      // Update PP spent
      const newPPSpent = (stats.ppSpent || 0) + ppCost;
      
      // Update or add skill usage
      const skillsUsed = [...(stats.skillsUsed || [])];
      const skillIndex = skillsUsed.findIndex(s => s.skillId === skillId);
      
      if (skillIndex >= 0) {
        // Update existing skill
        skillsUsed[skillIndex] = {
          ...skillsUsed[skillIndex],
          count: skillsUsed[skillIndex].count + 1,
          energyType: energyType || skillsUsed[skillIndex].energyType,
          totalDamage: (skillsUsed[skillIndex].totalDamage || 0) + (damage || 0),
          totalHealing: (skillsUsed[skillIndex].totalHealing || 0) + (healing || 0)
        };
      } else {
        // Add new skill
        skillsUsed.push({
          skillId,
          skillName,
          count: 1,
          energyType,
          totalDamage: damage || 0,
          totalHealing: healing || 0
        });
      }
      
      // damageDealt is updated only in trackDamage (health + shield) to avoid double-counting
      transaction.update(statsRef, {
        ppSpent: newPPSpent,
        skillsUsed,
        totalSkillsUsed: (stats.totalSkillsUsed || 0) + 1,
        healingGiven: (stats.healingGiven || 0) + (healing || 0)
      });
    });
    
    debug('inSessionStats', `Tracked skill usage: ${skillName} by ${playerId}`);
    skillUseDebug({
      eventId: truncateId(sessionId),
      playerId: truncateId(playerId),
      skillId,
      skillName,
      skillType: 'session_stats',
      energyType: energyType || 'unspecified',
      cost: ppCost,
      damageDealtRecorded: damage ?? 0,
      healingRecorded: healing ?? 0,
      source: 'trackSkillUsage',
    });
    return true;
  } catch (error) {
    debugError('inSessionStats', `Error tracking skill usage for ${playerId}`, error);
    return false;
  }
}

/**
 * Track damage dealt/taken
 */
export async function trackDamage(
  sessionId: string,
  attackerId: string,
  targetId: string,
  damage: number,
  shieldDamage?: number
): Promise<boolean> {
  try {
    await ensurePlayerStatsIfMissing(sessionId, attackerId, {});
    await ensurePlayerStatsIfMissing(sessionId, targetId, {});
    const attackerStatsRef = doc(db, 'inSessionRooms', sessionId, 'stats', attackerId);
    const targetStatsRef = doc(db, 'inSessionRooms', sessionId, 'stats', targetId);
    
    await runTransaction(db, async (transaction) => {
      // Update attacker's damage dealt
      const attackerStatsDoc = await transaction.get(attackerStatsRef);
      if (attackerStatsDoc.exists()) {
        const attackerStats = attackerStatsDoc.data() as SessionStats;
        transaction.update(attackerStatsRef, {
          damageDealt: (attackerStats.damageDealt || 0) + damage + (shieldDamage || 0)
        });
      }
      
      // Update target's damage taken
      const targetStatsDoc = await transaction.get(targetStatsRef);
      if (targetStatsDoc.exists()) {
        const targetStats = targetStatsDoc.data() as SessionStats;
        transaction.update(targetStatsRef, {
          damageTaken: (targetStats.damageTaken || 0) + damage + (shieldDamage || 0)
        });
      }
    });
    
    return true;
  } catch (error) {
    debugError('inSessionStats', `Error tracking damage`, error);
    return false;
  }
}

/**
 * Track an elimination. Counts at most once per elimination (a revive clears `isEliminated`).
 * Eliminator earns LIVE_EVENT_PP_BASE_PER_ELIMINATION (500, Flow multiplier applies) plus the eliminated
 * player's remaining in-event PP, all on the session row; {@link finalizeSessionStats} pays the row gain
 * to the account at session end.
 */
export async function trackElimination(
  sessionId: string,
  eliminatorId: string,
  eliminatedId: string
): Promise<boolean> {
  try {
    const eliminatorStatsRef = doc(db, 'inSessionRooms', sessionId, 'stats', eliminatorId);
    const eliminatedStatsRef = doc(db, 'inSessionRooms', sessionId, 'stats', eliminatedId);
    const sessionRef = doc(db, 'inSessionRooms', sessionId);

    let bounty = LIVE_EVENT_PP_BASE_PER_ELIMINATION;
    const sessionDocPre = await getDoc(sessionRef);
    if (sessionDocPre.exists()) {
      const players = (sessionDocPre.data()?.players || []) as Array<Record<string, unknown>>;
      const row = players.find((p) => p?.userId === eliminatorId);
      bounty = applyFlowPpRewardMultiplier(bounty, parseFlowStateFromPlayerRow(row));
    }

    // Firestore transactions require every read before any write.
    const counted = await runTransaction(db, async (transaction) => {
      const eliminatorStatsDoc = await transaction.get(eliminatorStatsRef);
      const eliminatedStatsDoc = await transaction.get(eliminatedStatsRef);
      if (eliminatedStatsDoc.exists() && (eliminatedStatsDoc.data() as SessionStats).isEliminated) {
        return false;
      }

      if (eliminatorStatsDoc.exists()) {
        const eliminatorStats = eliminatorStatsDoc.data() as SessionStats;
        transaction.update(eliminatorStatsRef, {
          eliminations: (eliminatorStats.eliminations || 0) + 1,
          ppEarned: (eliminatorStats.ppEarned || 0) + bounty,
        });
      } else {
        transaction.set(
          eliminatorStatsRef,
          {
            playerId: eliminatorId,
            eliminations: 1,
            ppEarned: bounty,
            vaultPpGrantedMidSession: 0,
            participationEarned: 0,
            movesEarned: 0,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      if (eliminatedStatsDoc.exists()) {
        transaction.update(eliminatedStatsRef, {
          isEliminated: true,
          eliminatedBy: eliminatorId
        });
      } else {
        transaction.set(
          eliminatedStatsRef,
          {
            playerId: eliminatedId,
            isEliminated: true,
            eliminatedBy: eliminatorId,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }
      return true;
    });

    if (!counted) {
      debug('inSessionStats', `Elimination of ${eliminatedId} already counted — no second bounty`);
      return false;
    }

    void trackPlayerAction(eliminatorId, 'DEFEAT_ENEMY', 1).catch((err) =>
      console.error('[inSessionStats] defeat_enemies daily challenge after elimination:', err)
    );

    // Session row: bounty + any target PP not yet moved by applyInSessionMove; strip target to siege floor (or 0).
    let leftoverOnTarget = 0;
    await runTransaction(db, async (tx) => {
      const sessionSnap = await tx.get(sessionRef);
      if (!sessionSnap.exists()) return;
      const players = [...(sessionSnap.data()?.players || [])] as Array<Record<string, unknown>>;
      const eliminatorIndex = players.findIndex((p) => p?.userId === eliminatorId);
      const eliminatedIndex = players.findIndex((p) => p?.userId === eliminatedId);
      if (eliminatorIndex < 0) return;

      leftoverOnTarget = 0;
      if (eliminatedIndex >= 0) {
        const elimRow = { ...players[eliminatedIndex] };
        const isSiege =
          elimRow.participationMode === 'offline' || elimRow.joinedViaSiege === true;
        const startPp = Math.max(
          0,
          Math.floor(Number(elimRow.liveEventStartingPP ?? elimRow.powerPoints) || 0)
        );
        const floor = isSiege ? computeSiegeProtectionFloor(startPp) : 0;
        const before = Math.max(0, Math.floor(Number(elimRow.powerPoints) || 0));
        leftoverOnTarget = Math.max(0, before - floor);
        elimRow.powerPoints = floor;
        elimRow.eliminated = true;
        elimRow.eliminatedBy = eliminatorId;
        players[eliminatedIndex] = elimRow;
      }

      const currentPP = Number(players[eliminatorIndex].powerPoints) || 0;
      players[eliminatorIndex] = {
        ...players[eliminatorIndex],
        powerPoints: currentPP + bounty + leftoverOnTarget,
      };
      tx.update(sessionRef, { players, updatedAt: serverTimestamp() });
    });

    debug('inSessionStats', `Tracked elimination: ${eliminatorId} eliminated ${eliminatedId} (+${bounty} bounty + ${leftoverOnTarget} leftover in-event PP)`);
    try {
      const { mstLiveLog } = await import('./mstLiveDebug');
      mstLiveLog('ELIMINATION', 'Elimination credited', {
        eventId: sessionId,
        userId: eliminatorId,
        eliminatedUid: eliminatedId,
        ppFromElimination: bounty + leftoverOnTarget,
      });
    } catch {
      /* ignore */
    }
    void awardPowerXpForElimination(eliminatorId, sessionId);
    return true;
  } catch (error) {
    debugError('inSessionStats', `Error tracking elimination`, error);
    return false;
  }
}

type RoomPlayerEliminationFields = {
  userId: string;
  displayName?: string;
  powerPoints?: number;
  participationCount?: number;
  movesEarned?: number;
  eliminated?: boolean;
  eliminatedBy?: string;
};

/**
 * Merge room.players[].eliminated (authoritative) into stats so the session summary lists eliminations
 * even when stats subdocs missed trackElimination or never existed.
 */
function mergeRoomEliminationsIntoStatsMap(
  statsMap: { [playerId: string]: SessionStats },
  roomPlayers: RoomPlayerEliminationFields[],
  sessionId: string,
  sessionStartTime: any,
  sessionEndTime: any,
  duration: number
): void {
  for (const rp of roomPlayers) {
    if (!rp?.userId || !rp.eliminated) continue;
    const uid = rp.userId;
    const eliminatedBy = rp.eliminatedBy || statsMap[uid]?.eliminatedBy;
    const existing = statsMap[uid];
    if (existing) {
      statsMap[uid] = {
        ...existing,
        isEliminated: true,
        ...(eliminatedBy ? { eliminatedBy } : {}),
        playerName: existing.playerName || rp.displayName || 'Player'
      };
    } else {
      const endingPP = rp.powerPoints ?? 0;
      statsMap[uid] = {
        playerId: uid,
        playerName: rp.displayName || 'Player',
        startingPP: endingPP,
        endingPP,
        netPPGained: 0,
        ppSpent: 0,
        ppEarned: 0,
        vaultPpGrantedMidSession: 0,
        participationEarned: rp.participationCount ?? 0,
        movesEarned: rp.movesEarned ?? 0,
        eliminations: 0,
        isEliminated: true,
        ...(eliminatedBy ? { eliminatedBy } : {}),
        damageDealt: 0,
        damageTaken: 0,
        healingGiven: 0,
        healingReceived: 0,
        skillsUsed: [],
        totalSkillsUsed: 0,
        sessionId,
        sessionStartTime,
        sessionEndTime,
        sessionDuration: duration
      };
    }
  }
}

/**
 * Client-side: enrich embedded sessionSummary with room snapshot (fixes older summaries / reconnects).
 */
export function mergeRoomEliminationsIntoSummary(
  summary: SessionSummary,
  roomPlayers: RoomPlayerEliminationFields[] | null | undefined
): SessionSummary {
  if (!roomPlayers?.length) return summary;
  const stats = { ...summary.stats };
  mergeRoomEliminationsIntoStatsMap(stats, roomPlayers, summary.sessionId, summary.startedAt, summary.endedAt, summary.duration);
  const mergedKeys = Object.keys(stats).length;
  return {
    ...summary,
    stats,
    totalPlayers: Math.max(summary.totalPlayers, mergedKeys)
  };
}

/**
 * Estimates elimination portion of ppEarned (same heuristic as SessionSummaryModal: total minus participation notional).
 */
export function estimateEliminationPpFromStats(stats: SessionStats): number {
  const partPP = (stats.participationEarned || 0) * LIVE_EVENT_PP_PER_PARTICIPATION_POINT;
  return Math.max(0, (stats.ppEarned || 0) - partPP);
}

async function deductPPFromStudentUserVault(userId: string, amount: number): Promise<void> {
  if (amount <= 0) return;
  try {
    const { applyPlayerPPDelta } = await import('./playerPowerPoints');
    await applyPlayerPPDelta(userId, -amount, {
      mode: 'award',
      meta: {
        sourceType: 'liveEvent',
        sourceId: 'live-event-elimination',
        notes: 'Live Event elimination PP loss',
      },
    });
    debug('inSessionStats', `Elimination PP penalty: deducted ${amount} PP from accounts for ${userId}`);
  } catch (e) {
    debugError('inSessionStats', `deductPPFromStudentUserVault failed for ${userId}`, e);
  }
}

/**
 * Credits PP to vault / students / users atomically. Teacher award: not capped at vault capacity
 * (overflow is trimmed at the next generator grant). Throws on failure so callers can retry.
 */
export async function creditPPToStudentUserVault(
  userId: string,
  amount: number,
  notes?: string
): Promise<void> {
  if (amount <= 0) return;
  try {
    const { applyPlayerPPDelta } = await import('./playerPowerPoints');
    await applyPlayerPPDelta(userId, amount, {
      mode: 'award',
      meta: {
        sourceType: 'liveEvent',
        sourceId: 'live-event-credit',
        notes: notes || 'Live Event PP credit',
      },
    });
  } catch (e) {
    debugError('inSessionStats', `creditPP failed for ${userId}`, e);
    throw e;
  }

  debug('inSessionStats', `Credited ${amount} PP to accounts for ${userId}`);
}

/**
 * Record PP already written to vault/students during the live session (elimination bounty, sprint vault bonus)
 * so {@link finalizeSessionStats} can credit the remainder without double-counting.
 */
export async function addVaultPpGrantedMidSessionStat(
  sessionId: string,
  playerId: string,
  amount: number
): Promise<void> {
  if (amount <= 0) return;
  try {
    const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
    await updateDoc(statsRef, { vaultPpGrantedMidSession: increment(amount) });
  } catch (e) {
    debugError('inSessionStats', `addVaultPpGrantedMidSessionStat failed for ${playerId}`, e);
  }
}

/**
 * Halves quiz + stats PP for eliminated players, deducts vault/student/user for quiz + elimination portions that were actually granted.
 */
async function applyEliminatedPlayerPointPenalty(
  sessionId: string,
  statsMap: { [playerId: string]: SessionStats },
  quizPpByPlayer: Record<string, number> | undefined
): Promise<Record<string, number>> {
  const out: Record<string, number> = { ...(quizPpByPlayer ?? {}) };
  const frac = LIVE_EVENT_ELIMINATED_PP_FRACTION;

  for (const [uid, stats] of Object.entries(statsMap)) {
    if (!stats.isEliminated) continue;
    if (stats.eliminationPenaltyApplied) {
      // Already deducted by an earlier finalize; only re-derive the halved totals for this summary.
      out[uid] = Math.floor((out[uid] ?? 0) * frac);
      const net = Math.floor((stats.netPPGained ?? 0) * frac);
      statsMap[uid] = { ...stats, netPPGained: net, endingPP: Math.max(0, (stats.startingPP ?? 0) + net) };
      continue;
    }

    const quizPP = out[uid] ?? 0;
    const pe = stats.ppEarned ?? 0;
    const elimPP = estimateEliminationPpFromStats(stats);

    const newQuiz = Math.floor(quizPP * frac);
    const newPe = Math.floor(pe * frac);
    const newElimPP = Math.floor(elimPP * frac);
    const oldNet = stats.netPPGained ?? 0;
    const newNet = Math.floor(oldNet * frac);
    const startingPP = stats.startingPP ?? 0;
    const newEndingPP = Math.max(0, startingPP + newNet);

    out[uid] = newQuiz;

    const deductQuiz = quizPP - newQuiz;
    // Elimination PP still on the session row is halved through netPPGained; only PP already credited
    // to the account mid-session needs an account deduction.
    const deductElim = Math.min(elimPP - newElimPP, Math.max(0, stats.vaultPpGrantedMidSession ?? 0));
    const totalDeduct = deductQuiz + deductElim;

    statsMap[uid] = {
      ...stats,
      ppEarned: newPe,
      netPPGained: newNet,
      endingPP: newEndingPP,
      eliminationPenaltyApplied: true,
    };

    // Mark first so a concurrent finalize cannot halve and deduct a second time.
    let claimed = false;
    try {
      const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', uid);
      claimed = await runTransaction(db, async (tx) => {
        const snap = await tx.get(statsRef);
        if (snap.exists() && (snap.data() as SessionStats).eliminationPenaltyApplied) return false;
        tx.set(
          statsRef,
          { ppEarned: newPe, netPPGained: newNet, endingPP: newEndingPP, eliminationPenaltyApplied: true },
          { merge: true }
        );
        return true;
      });
    } catch (e) {
      debugError('inSessionStats', `Failed to persist penalty stats for ${uid}`, e);
    }

    if (claimed && totalDeduct > 0) {
      await deductPPFromStudentUserVault(uid, totalDeduct);
    }
  }

  return out;
}

/**
 * Track participation earned (single hub for Live Event “success streak” / Flow State).
 * Correct quiz answers, class-flow sprint rewards, and host-awarded participation all call this.
 * Updates stats + session `players[]` with `successStreak`, `flowStateActive`, `flowStateNonce` (see liveEventFlowState).
 */
export async function trackParticipation(
  sessionId: string,
  playerId: string,
  participationAmount: number,
  options?: { playerDisplayName?: string; eventEnergyType?: string; skipStreakIncrement?: boolean }
): Promise<boolean> {
  try {
    // Ensure stats exist — silent early-return used to drop moves after correct answers
    await ensurePlayerStatsIfMissing(sessionId, playerId, {
      playerName: options?.playerDisplayName,
    });

    const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
    const sessionRef = doc(db, 'inSessionRooms', sessionId);

    let streakLogLine: string | null = null;
    let nextConsecutive = 0;
    let flowEnteredThisCall = false;

    await runTransaction(db, async (transaction) => {
      const statsDoc = await transaction.get(statsRef);
      const sessionDoc = await transaction.get(sessionRef);

      const stats = (statsDoc.exists() ? statsDoc.data() : {}) as SessionStats;
      const newParticipation = (stats.participationEarned || 0) + participationAmount;
      const newMovesEarned = Math.floor(newParticipation / 1); // 1 participation = 1 move
      let ppFromParticipation = participationAmount * LIVE_EVENT_PP_PER_PARTICIPATION_POINT;

      const prevConsecutive = stats.consecutiveParticipationAwards ?? 0;
      const name = options?.playerDisplayName || stats.playerName || 'Player';
      if (options?.skipStreakIncrement) {
        nextConsecutive = prevConsecutive;
      } else {
        const streakState = { consecutiveAwards: prevConsecutive };
        const { next, battleLogLine } = applyParticipationStreakAward(streakState, name, participationAmount);
        nextConsecutive = next.consecutiveAwards;
        streakLogLine = battleLogLine;
      }

      const sessionRow = sessionDoc.exists()
        ? (sessionDoc.data() as Record<string, unknown>)
        : {};
      const eventEnergyType =
        options?.eventEnergyType ||
        inferEnergyTypeForLiveEvent({
          energyType: sessionRow.energyType as string | undefined,
          liveEventMode: sessionRow.liveEventMode as string | undefined,
          energyTypeAwarded: sessionRow.energyTypeAwarded as import('../types/season1').EnergyType | undefined,
          neutralFlowEnergyType: sessionRow.neutralFlowEnergyType as import('../types/season1').EnergyType | undefined,
        });
      const prevTotals = stats.participationEnergyTotals || {};
      const nextEnergyTotals = {
        ...prevTotals,
        [eventEnergyType]: (prevTotals[eventEnergyType] || 0) + participationAmount,
      };

      let flowParsed = parseFlowStateFromPlayerRow(null);
      if (sessionDoc.exists()) {
        const playersForFlow = [...((sessionDoc.data()?.players || []) as Array<Record<string, unknown>>)];
        const flowRow = playersForFlow.find((p) => p && (p as { userId?: string }).userId === playerId);
        flowParsed = parseFlowStateFromPlayerRow(flowRow);
        if (!options?.skipStreakIncrement) {
          const newly = getNewlyReachedBoonThresholds(
            prevConsecutive,
            nextConsecutive,
            flowParsed.claimedThresholds
          );
          if (newly.length > 0 && flowParsed.pendingThreshold == null) {
            flowParsed = {
              ...flowParsed,
              pendingThreshold: Math.min(...newly) as import('../types/liveEventFlowBoons').FlowBoonThreshold,
              activated: true,
            };
          }
        }
      }
      ppFromParticipation = applyFlowPpRewardMultiplier(ppFromParticipation, flowParsed);
      const newPPEarned = (stats.ppEarned || 0) + ppFromParticipation;

      const statsPayload = {
        playerId,
        playerName: name,
        participationEarned: newParticipation,
        movesEarned: newMovesEarned,
        ppEarned: newPPEarned,
        consecutiveParticipationAwards: nextConsecutive,
        lastLoggedStreakCount: nextConsecutive,
        participationEnergyTotals: nextEnergyTotals,
        updatedAt: serverTimestamp(),
      };
      if (statsDoc.exists()) {
        transaction.update(statsRef, statsPayload);
      } else {
        transaction.set(statsRef, statsPayload, { merge: true });
      }

      // Session row PP is what MST MKT and finalizeSessionStats use; mirror participation awards here
      // so players can spend earned PP during the Live Event (same rate as stats.ppEarned).
      if (sessionDoc.exists()) {
        const sessionPatch: {
          players?: unknown[];
          battleLog?: ReturnType<typeof arrayUnion>;
          updatedAt: ReturnType<typeof serverTimestamp>;
        } = { updatedAt: serverTimestamp() };
        const players = [...((sessionDoc.data()?.players || []) as Array<Record<string, unknown>>)];
        const pIdx = players.findIndex((p) => p && (p as { userId?: string }).userId === playerId);
        if (pIdx >= 0) {
          const row = { ...players[pIdx] } as Record<string, unknown>;
          const flowEval = evaluateFlowStateAfterSuccess(row, prevConsecutive, nextConsecutive);
          const { flowEntered, ...flowForStore } = flowEval;
          if (flowEntered) flowEnteredThisCall = true;
          row.powerPoints = Math.max(0, (Number(row.powerPoints) || 0) + ppFromParticipation);
          row.participationCount = Math.max(0, (Number(row.participationCount) || 0) + participationAmount);
          row.movesEarned = Math.max(0, (Number(row.movesEarned) || 0) + participationAmount);
          row.flowState = flowStateToFirestore(flowParsed);
          players[pIdx] = { ...row, ...flowForStore } as (typeof players)[number];
          sessionPatch.players = players;
        }
        if (streakLogLine) {
          sessionPatch.battleLog = arrayUnion(streakLogLine);
        }
        if (sessionPatch.players || sessionPatch.battleLog) {
          transaction.update(sessionRef, sessionPatch);
        }
      } else if (streakLogLine) {
        transaction.update(sessionRef, {
          battleLog: arrayUnion(streakLogLine),
          updatedAt: serverTimestamp(),
        });
      }
    });

    if (flowEnteredThisCall) {
      void unlockLevel2BuilderFromLiveFlow(playerId).catch((e) => {
        debugError('inSessionStats', 'Level 2 manifest unlock after Flow State failed', e);
      });
    }

    return true;
  } catch (error) {
    debugError('inSessionStats', `Error tracking participation`, error);
    return false;
  }
}

/** Call when a player fails a streak-eligible action (e.g. wrong quiz answer). */
export async function breakParticipationStreak(
  sessionId: string,
  playerId: string,
  playerDisplayName?: string
): Promise<void> {
  try {
    const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
    const sessionRef = doc(db, 'inSessionRooms', sessionId);

    await runTransaction(db, async (transaction) => {
      const statsDoc = await transaction.get(statsRef);
      if (!statsDoc.exists()) return;
      const stats = statsDoc.data() as SessionStats;
      const prev = stats.consecutiveParticipationAwards ?? 0;
      const hadStreak = prev >= 3;
      const name = playerDisplayName || stats.playerName || 'Player';
      const msg = breakParticipationStreakMessage(name, hadStreak);

      transaction.update(statsRef, {
        consecutiveParticipationAwards: 0,
        lastLoggedStreakCount: 0,
      });

      const sessionDoc = await transaction.get(sessionRef);
      if (!sessionDoc.exists()) return;

      const players = [...((sessionDoc.data()?.players || []) as Array<Record<string, unknown>>)];
      const pIdx = players.findIndex((p) => p && (p as { userId?: string }).userId === playerId);
      const patch: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
      };
      if (msg) {
        patch.battleLog = arrayUnion(msg);
      }
      if (pIdx >= 0) {
        const row = { ...players[pIdx] } as Record<string, unknown>;
        const flowClear = mergeFlowClearIntoRow(row);
        const flowParsed = parseFlowStateFromPlayerRow(row);
        const clearedFlow = {
          ...flowParsed,
          activated: false,
          pendingThreshold: null,
          ppMultiplier: 1 as const,
          questionPointMultiplier: 1 as const,
          damageBoostPercent: 0 as const,
        };
        players[pIdx] = {
          ...row,
          ...flowClear,
          flowState: flowStateToFirestore(clearedFlow),
        } as (typeof players)[number];
        patch.players = players;
      }
      if (patch.battleLog !== undefined || patch.players !== undefined) {
        // Firestore UpdateData is strict; session row shape is dynamic (players[]).
        transaction.update(sessionRef, patch as Record<string, unknown> as never);
      }
    });
  } catch (error) {
    debugError('inSessionStats', `Error breaking participation streak`, error);
  }
}

/**
 * Battle / Team Battle Royale live quiz: one incorrect answer removes one Participation Power
 * (session `movesEarned` / `participationCount`) and mirrors the same session PP delta as losing
 * one participation point ({@link LIVE_EVENT_PP_PER_PARTICIPATION_POINT}), without touching streak logic
 * (caller should run {@link breakParticipationStreak} first).
 */
export async function deductParticipationPowerForBattleQuizIncorrect(
  sessionId: string,
  playerId: string,
  playerDisplayName?: string
): Promise<void> {
  try {
    const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
    const sessionRef = doc(db, 'inSessionRooms', sessionId);
    const ppDelta = LIVE_EVENT_PP_PER_PARTICIPATION_POINT;

    await runTransaction(db, async (transaction) => {
      const statsDoc = await transaction.get(statsRef);
      const sessionDoc = await transaction.get(sessionRef);
      if (!sessionDoc.exists()) return;

      const name = playerDisplayName || 'Player';
      const logLine = `⚠️ ${name} lost 1 Participation Power (incorrect quiz answer).`;

      const players = [...((sessionDoc.data()?.players || []) as Array<Record<string, unknown>>)];
      const pIdx = players.findIndex((p) => p && (p as { userId?: string }).userId === playerId);
      if (pIdx < 0) return;

      if (statsDoc.exists()) {
        const stats = statsDoc.data() as SessionStats;
        const nextParticipation = Math.max(0, (stats.participationEarned || 0) - 1);
        const nextMoves = Math.max(0, (stats.movesEarned || 0) - 1);
        const nextPpEarned = Math.max(0, (stats.ppEarned || 0) - ppDelta);
        transaction.update(statsRef, {
          participationEarned: nextParticipation,
          movesEarned: nextMoves,
          ppEarned: nextPpEarned,
        });
      }

      const row = { ...players[pIdx] } as Record<string, unknown>;
      const moves = Math.max(0, Math.floor(Number(row.movesEarned) || 0) - 1);
      const partCount = Math.max(0, Math.floor(Number(row.participationCount) || 0) - 1);
      const pp = Math.max(0, Math.floor(Number(row.powerPoints) || 0) - ppDelta);
      players[pIdx] = {
        ...row,
        movesEarned: moves,
        participationCount: partCount,
        powerPoints: pp,
      } as (typeof players)[number];

      transaction.update(sessionRef, {
        players,
        battleLog: arrayUnion(logLine),
        updatedAt: serverTimestamp(),
      } as Record<string, unknown> as never);
    });
  } catch (error) {
    debugError('inSessionStats', 'deductParticipationPowerForBattleQuizIncorrect', error);
  }
}

/** UIDs that should receive session-end Power XP (roster + optional co-op `participantRecords`). */
function collectLiveEventRewardPlayerIds(sessionData: Record<string, unknown>, fallbackPlayerIds: string[]): string[] {
  const fromPlayers = Array.isArray(sessionData.players)
    ? (sessionData.players as { userId?: string }[])
        .map((p) => p?.userId)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];
  const pr = sessionData.participantRecords as
    | Record<string, { userId?: string; participantId?: string }>
    | undefined;
  const fromPr =
    pr && typeof pr === 'object' && !Array.isArray(pr)
      ? Object.values(pr)
          .map((r) => (typeof r?.userId === 'string' ? r.userId : r?.participantId))
          .filter((id): id is string => typeof id === 'string' && id.length > 0)
      : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of [...fromPlayers, ...fromPr, ...fallbackPlayerIds]) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out.length > 0 ? out : [...fallbackPlayerIds];
}

function buildSyntheticSessionStats(
  playerId: string,
  sessionId: string,
  sessionData: Record<string, unknown>,
  sessionStartTime: unknown,
  sessionEndTime: unknown,
  duration: number
): SessionStats {
  type PRow = {
    userId?: string;
    displayName?: string;
    powerPoints?: number;
    participationCount?: number;
    movesEarned?: number;
    eliminated?: boolean;
  };
  const players = (sessionData.players as PRow[] | undefined) || [];
  const prow = players.find((p) => p?.userId === playerId);
  return {
    playerId,
    playerName: prow?.displayName || 'Player',
    startingPP: typeof prow?.powerPoints === 'number' ? prow.powerPoints : 0,
    endingPP: typeof prow?.powerPoints === 'number' ? prow.powerPoints : 0,
    netPPGained: 0,
    ppSpent: 0,
    ppEarned: 0,
    vaultPpGrantedMidSession: 0,
    participationEarned: typeof prow?.participationCount === 'number' ? prow.participationCount : 0,
    movesEarned: typeof prow?.movesEarned === 'number' ? prow.movesEarned : 0,
    eliminations: 0,
    isEliminated: !!prow?.eliminated,
    damageDealt: 0,
    damageTaken: 0,
    healingGiven: 0,
    healingReceived: 0,
    skillsUsed: [],
    totalSkillsUsed: 0,
    sessionId,
    sessionStartTime,
    sessionEndTime,
    sessionDuration: duration,
  };
}

/**
 * Finalize session stats when session ends
 */
export async function finalizeSessionStats(
  sessionId: string,
  playerIds: string[]
): Promise<SessionSummary | null> {
  try {
    const sessionRef = doc(db, 'inSessionRooms', sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (!sessionDoc.exists()) {
      debugError('inSessionStats', `Session ${sessionId} does not exist`);
      return null;
    }
    
    const sessionData = sessionDoc.data() as Record<string, unknown>;
    const existingSummary = sessionData.sessionSummary as SessionSummary | undefined;
    if (sessionData.status === 'ended' && existingSummary) {
      return existingSummary;
    }
    const sessionStartTime = sessionData.startedAt || sessionData.createdAt;
    const rewardPlayerIds = collectLiveEventRewardPlayerIds(sessionData, playerIds);
    const sessionEndTime = serverTimestamp();
    
    // Calculate duration
    let duration = 0;
    if (sessionStartTime) {
      const st = sessionStartTime as { toMillis?: () => number };
      const start = typeof st.toMillis === 'function' ? st.toMillis() : new Date(sessionStartTime as string | number | Date).getTime();
      const end = Date.now();
      duration = Math.floor((end - start) / 1000); // Duration in seconds
    }
    
    // Get all player stats (include roster + co-op participants; synthesize missing stats docs for Power XP)
    const statsMap: { [playerId: string]: SessionStats } = {};

    for (const playerId of rewardPlayerIds) {
      const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
      const statsDoc = await getDoc(statsRef);

      if (statsDoc.exists()) {
        const stats = statsDoc.data() as SessionStats;

        const player = ((sessionData.players || []) as Array<{ userId?: string; powerPoints?: number }>).find(
          (p) => p.userId === playerId
        );
        const endingPP = player?.powerPoints || stats.endingPP || stats.startingPP;

        const netPPGained = endingPP - stats.startingPP;

        const finalizedStats: SessionStats = {
          ...stats,
          endingPP,
          netPPGained,
          sessionEndTime,
          sessionDuration: duration,
        };

        await updateDoc(statsRef, {
          endingPP,
          netPPGained,
          sessionEndTime,
          sessionDuration: duration,
        });

        statsMap[playerId] = finalizedStats;
      } else {
        statsMap[playerId] = buildSyntheticSessionStats(
          playerId,
          sessionId,
          sessionData,
          sessionStartTime,
          sessionEndTime,
          duration
        );
      }
    }

    mergeRoomEliminationsIntoStatsMap(
      statsMap,
      (sessionData.players || []) as RoomPlayerEliminationFields[],
      sessionId,
      sessionStartTime,
      sessionEndTime,
      duration
    );

    const rawQuizPpByPlayer = (sessionData.lastQuizPpByPlayer || {}) as Record<string, number>;
    const adjustedQuizPpByPlayer = await applyEliminatedPlayerPointPenalty(
      sessionId,
      statsMap,
      rawQuizPpByPlayer
    );

    /**
     * Host cannot write other users' `students`/`users` docs (Firestore rules). Write each player's owed PP on their
     * stats subdoc; the player applies it via {@link claimLiveEventSessionEndPendingPp}.
     * Same for session-end Power + Battle Pass XP: {@link claimLiveEventSessionEndPowerAndBattlePass}.
     */
    const sessionEndPayoutIds = Array.from(new Set([...rewardPlayerIds, ...Object.keys(statsMap)]));
    for (const playerId of sessionEndPayoutIds) {
      const st = statsMap[playerId];
      if (!st) continue;
      const alreadyVault = st.vaultPpGrantedMidSession ?? 0;
      const net = st.netPPGained ?? 0;
      const transfer = Math.max(0, net - alreadyVault);
      const priorPending =
        typeof st.sessionEndAccountPpPending === 'number' && st.sessionEndAccountPpPending > 0
          ? st.sessionEndAccountPpPending
          : 0;
      const totalPending = priorPending + transfer;
      if (totalPending <= 0) continue;
      try {
        const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
        // The session-end transfer is added to pending exactly once per player, even if finalize runs twice.
        const stored = await runTransaction(db, async (tx) => {
          const snap = await tx.get(statsRef);
          if (!snap.exists()) return null;
          const live = snap.data() as SessionStats;
          if (live.sessionEndTransferRecorded) return null;
          // Preserve mid-session queued amounts (e.g. sprint vault PP host could not write to students/).
          const livePrior =
            typeof live.sessionEndAccountPpPending === 'number'
              ? Math.max(0, Number(live.sessionEndAccountPpPending) || 0)
              : live.sessionEndAccountPpClaimedAt
                ? 0
                : priorPending;
          tx.update(statsRef, {
            sessionEndAccountPpPending: livePrior + transfer,
            sessionEndTransferRecorded: true,
            // A mid-session queued claim may already have been taken; reopen it for the session-end amount.
            ...(live.sessionEndAccountPpClaimedAt && livePrior + transfer > 0
              ? { sessionEndAccountPpClaimedAt: deleteField() }
              : {}),
          });
          return livePrior;
        });
        if (stored === null) continue;
        debug(
          'inSessionStats',
          `Live event end: stored ${stored + transfer} PP pending claim (prior ${stored} + transfer ${transfer}; net ${net} − mid-session vault ${alreadyVault}) for ${playerId}`
        );
      } catch (e) {
        debugError('inSessionStats', `Live event end pending PP write failed for ${playerId}`, e);
      }
    }

    const quizSessionRef = doc(db, 'inSessionRooms', sessionId, 'quizSession', 'current');
    const quizSessionSnap = await getDoc(quizSessionRef);
    let correctByPlayer: Record<string, number> = {};
    let leaderboard: Record<string, number> = {};
    let quizGameMode: string | undefined;
    let quizTitleFromSession: string | undefined;
    if (quizSessionSnap.exists()) {
      const qd = quizSessionSnap.data() as {
        correctCount?: Record<string, number>;
        leaderboard?: Record<string, number>;
        gameMode?: string;
        quizTitle?: string;
      };
      correctByPlayer = { ...(qd.correctCount || {}) };
      leaderboard = { ...(qd.leaderboard || {}) };
      quizGameMode = qd.gameMode;
      if (typeof qd.quizTitle === 'string' && qd.quizTitle.trim()) quizTitleFromSession = qd.quizTitle.trim();
    }

    const roomPlayersList = (sessionData.players || []) as Array<{ userId: string }>;
    const quizScoreUids = new Set<string>();
    roomPlayersList.forEach((p) => p?.userId && quizScoreUids.add(p.userId));
    Object.keys(leaderboard).forEach((uid) => quizScoreUids.add(uid));
    const sortedByQuizScore = Array.from(quizScoreUids).sort(
      (a, b) => (leaderboard[b] ?? 0) - (leaderboard[a] ?? 0)
    );
    const rankByPlayer: Record<string, number> = {};
    sortedByQuizScore.forEach((uid, idx) => {
      rankByPlayer[uid] = idx + 1;
    });
    const totalRanked = Math.max(1, sortedByQuizScore.length);

    const liveEventPowerGains: Record<string, LiveEventPowerGain> = {};
    const powerAwardPlayerIds = Array.from(new Set([...rewardPlayerIds, ...Object.keys(statsMap)]));
    for (const playerId of powerAwardPlayerIds) {
      const st = statsMap[playerId];
      if (!st) continue;
      const { branch, amount } = computeSessionEndPowerXp({
        liveEventMode: sessionData.liveEventMode as string | undefined,
        stats: st,
        correctAnswers: correctByPlayer[playerId] ?? 0,
        leaderboardScore: leaderboard[playerId] ?? 0,
        rankByScore: rankByPlayer[playerId] ?? totalRanked,
        totalRanked,
        quizPlacementPp: adjustedQuizPpByPlayer[playerId] ?? 0,
        quizGameMode,
      });
      if (amount <= 0) continue;
      const gain: LiveEventPowerGain =
        branch === 'physical'
          ? { physical: amount }
          : branch === 'mental'
            ? { mental: amount }
            : branch === 'emotional'
              ? { emotional: amount }
              : { spiritual: amount };

      // Host cannot update other students' `students` docs — mirror PP: queue on stats for self-claim.
      try {
        const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
        const snap = await getDoc(statsRef);
        if (!snap.exists()) {
          debug(
            'inSessionStats',
            `Live event end: no stats subdoc for ${playerId}, cannot queue power/BP pending (synthetic roster?)`
          );
          continue;
        }
        await updateDoc(statsRef, {
          sessionEndBattlePassXpPending: amount,
          sessionEndPowerGainPending: gain,
        });
        liveEventPowerGains[playerId] = gain;
        debug(
          'inSessionStats',
          `Live event end: queued ${amount} BP XP + power gain for ${playerId} (sessionEnd pending claim)`
        );
      } catch (e) {
        debugError('inSessionStats', `Live event end pending power/BP write failed for ${playerId}`, e);
      }
    }

    for (const playerId of sessionEndPayoutIds) {
      // Host cannot write other students' dailyChallenges — queue for self-claim.
      try {
        const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
        await setDoc(
          statsRef,
          { sessionEndParticipateChallengePending: true },
          { merge: true }
        );
      } catch (e) {
        debugError('inSessionStats', 'queue participate challenge pending failed', e);
      }
      try {
        const { registerLiveEventPendingClaim } = await import('./liveEventPendingClaimsService');
        await registerLiveEventPendingClaim(sessionId, playerId);
      } catch (e) {
        debugError('inSessionStats', 'registerLiveEventPendingClaim failed', e);
      }
    }

    // Sync each player's vault health and shield from session state so Live Event impact persists globally
    const players = (Array.isArray(sessionData.players) ? sessionData.players : []) as Array<{
      userId?: string;
      displayName?: string;
      hp?: number;
      shield?: number;
    }>;
    for (const p of players) {
      const uid = p.userId;
      if (!uid) continue;
      const hp = p.hp;
      const shield = p.shield;
      if (hp === undefined && shield === undefined) continue;
      try {
        const vaultRef = doc(db, 'vaults', uid);
        const updates: { vaultHealth?: number; shieldStrength?: number } = {};
        if (hp !== undefined) updates.vaultHealth = Math.max(0, hp);
        if (shield !== undefined) updates.shieldStrength = Math.max(0, shield);
        if (Object.keys(updates).length > 0) {
          await updateDoc(vaultRef, updates);
          debug('inSessionStats', `Synced vault for ${p.displayName || uid}: vaultHealth=${updates.vaultHealth ?? '—'}, shieldStrength=${updates.shieldStrength ?? '—'}`);
        }
      } catch (err) {
        debugError('inSessionStats', `Failed to sync vault for ${uid} at session end`, err);
      }
    }

    // Calculate MVP badges
    let mostPP = 0;
    let mostEliminations = 0;
    let mostParticipation = 0;
    let mostDamage = 0;
    
    let mvpPPPlayer: string | undefined;
    let mvpEliminationsPlayer: string | undefined;
    let mvpParticipationPlayer: string | undefined;
    let mvpDamagePlayer: string | undefined;
    
    for (const [playerId, stats] of Object.entries(statsMap)) {
      if (stats.netPPGained > mostPP) {
        mostPP = stats.netPPGained;
        mvpPPPlayer = playerId;
      }
      if (stats.eliminations > mostEliminations) {
        mostEliminations = stats.eliminations;
        mvpEliminationsPlayer = playerId;
      }
      if (stats.participationEarned > mostParticipation) {
        mostParticipation = stats.participationEarned;
        mvpParticipationPlayer = playerId;
      }
      if (stats.damageDealt > mostDamage) {
        mostDamage = stats.damageDealt;
        mvpDamagePlayer = playerId;
      }
    }
    
    // Assign badges
    for (const [playerId, stats] of Object.entries(statsMap)) {
      const badges: Array<{ type: string; label: string }> = [];
      
      if (playerId === mvpPPPlayer && mostPP > 0) {
        badges.push({ type: 'most_pp', label: '💰 Most PP Earned' });
      }
      if (playerId === mvpEliminationsPlayer && mostEliminations > 0) {
        badges.push({ type: 'most_eliminations', label: '⚔️ Most Eliminations' });
      }
      if (playerId === mvpParticipationPlayer && mostParticipation > 0) {
        badges.push({ type: 'most_participation', label: '✨ Most Participation' });
      }
      if (playerId === mvpDamagePlayer && mostDamage > 0) {
        badges.push({ type: 'most_damage', label: '💥 Most Damage Dealt' });
      }
      if (!stats.isEliminated) {
        badges.push({ type: 'survivor', label: '🛡️ Survivor' });
      }
      
      if (badges.length > 0) {
        statsMap[playerId].badges = badges as any;
        const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
        try {
          const snap = await getDoc(statsRef);
          if (snap.exists()) await updateDoc(statsRef, { badges });
        } catch {
          /* synthetic-only roster row: no stats subdoc */
        }
      }
    }
    
    // Determine overall MVP (prioritize eliminations, then PP)
    const mvpPlayerId = mvpEliminationsPlayer || mvpPPPlayer;

    /** Ranks for school leaderboard: only when quiz leaderboard had at least one positive score. */
    let liveEventQuizRankByPlayer: Record<string, number> | undefined;
    if (Object.keys(leaderboard).length > 0) {
      const scores = sortedByQuizScore.map((uid) => leaderboard[uid] ?? 0);
      const maxScore = scores.length > 0 ? Math.max(0, ...scores) : 0;
      if (maxScore > 0) {
        const byPlayer: Record<string, number> = {};
        sortedByQuizScore.forEach((uid, idx) => {
          if (statsMap[uid]) {
            byPlayer[uid] = idx + 1;
          }
        });
        if (Object.keys(byPlayer).length > 0) {
          liveEventQuizRankByPlayer = byPlayer;
        }
      }
    }
    
    const lastSnap = sessionData.lastQuizAwardsSnapshot as { quizTitle?: string } | null | undefined;
    const quizTitleFromAwards =
      lastSnap && typeof lastSnap === 'object' && typeof lastSnap.quizTitle === 'string'
        ? lastSnap.quizTitle.trim()
        : undefined;

    const sessionActivity = buildSessionActivitySummary({
      battleLog: sessionData.battleLog,
      liveEventMode: sessionData.liveEventMode as string | undefined,
      neutralFlowEnergyType: sessionData.neutralFlowEnergyType as string | undefined,
      classFlowSprintRaw: sessionData.classFlowSprint,
      sessionPlayerCount: Array.isArray(sessionData.players) ? sessionData.players.length : 0,
      statsMap,
      quizTitle: quizTitleFromSession || quizTitleFromAwards,
      quizGameMode,
      correctByPlayer,
      leaderboard,
      quizPpByPlayer: adjustedQuizPpByPlayer,
      liveEventQuizRankByPlayer,
    });

    const summaryHostUid =
      (typeof sessionData.hostUid === 'string' && sessionData.hostUid) ||
      (typeof sessionData.teacherId === 'string' && sessionData.teacherId) ||
      '';
    const summaryClassIds = Array.from(
      new Set(
        [sessionData.classId, ...(Array.isArray(sessionData.classIds) ? sessionData.classIds : [])]
          .map((c) => (typeof c === 'string' ? c.trim() : ''))
          .filter(Boolean)
      )
    );
    const roomPlayers = (sessionData.players || []) as Array<{
      userId: string;
      displayName?: string;
      classId?: string | null;
      isTeacher?: boolean;
    }>;
    const summaryPlayers = roomPlayers.length
      ? roomPlayers
      : Object.values(statsMap).map((s) => ({
          userId: s.playerId,
          displayName: s.playerName,
        }));

    let classResolution: Awaited<
      ReturnType<typeof import('./liveEventClassResolution').resolveLiveEventPlayerClasses>
    > | null = null;
    try {
      const { resolveLiveEventPlayerClasses } = await import('./liveEventClassResolution');
      classResolution = await resolveLiveEventPlayerClasses({
        roomClassId: typeof sessionData.classId === 'string' ? sessionData.classId : null,
        classIds: summaryClassIds,
        hostUid: summaryHostUid,
        players: summaryPlayers,
      });
    } catch (classErr) {
      debugError('inSessionStats', 'Class resolution failed (non-fatal)', classErr);
    }

    let workSummary: SessionSummary['workSummary'];
    try {
      const { buildLiveEventWorkSummary } = await import('./workBoardService');
      const gameTime = sessionData.gameTime as { workPeriodId?: string | null } | undefined;
      const built = await buildLiveEventWorkSummary({
        sessionId,
        classId: typeof sessionData.classId === 'string' ? sessionData.classId : null,
        workPeriodId: gameTime?.workPeriodId ?? null,
        players: summaryPlayers,
        hostUid: summaryHostUid,
        playerClassIds: classResolution?.classIdByPlayer,
      });
      if (built) workSummary = built;
    } catch (workErr) {
      debugError('inSessionStats', 'Work summary build failed (non-fatal)', workErr);
    }

    // Create session summary (include quiz awards if stored when a quiz completed)
    const summary: SessionSummary = {
      sessionId,
      classId: String(sessionData.classId ?? ''),
      className: String(sessionData.className ?? ''),
      startedAt: sessionStartTime,
      endedAt: sessionEndTime,
      duration,
      totalPlayers: Math.max(rewardPlayerIds.length, Object.keys(statsMap).length),
      stats: statsMap,
      mvpPlayerId,
      /** False when session-end power/BP was queued for student claim (see `sessionEnd*Pending` on stats). */
      liveEventPowerApplied: Object.keys(liveEventPowerGains).length === 0,
      ...(Object.keys(liveEventPowerGains).length > 0 && { liveEventPowerGains }),
      ...(sessionData.lastQuizAwardsSnapshot &&
      typeof sessionData.lastQuizAwardsSnapshot === 'object' &&
      sessionData.lastQuizAwardsSnapshot !== null
        ? { quizAwardsSnapshot: sessionData.lastQuizAwardsSnapshot as SessionSummary['quizAwardsSnapshot'] }
        : {}),
      ...(Object.keys(adjustedQuizPpByPlayer).length > 0 && { quizPpByPlayer: adjustedQuizPpByPlayer }),
      ...(liveEventQuizRankByPlayer && { liveEventQuizRankByPlayer }),
      sessionActivity,
      ...(workSummary ? { workSummary } : {}),
      ...(summaryHostUid ? { hostUid: summaryHostUid } : {}),
      ...(summaryClassIds.length ? { classIds: summaryClassIds } : {}),
      ...(classResolution
        ? {
            playerClassIds: classResolution.classIdByPlayer,
            classNames: classResolution.classNameById,
          }
        : {}),
    };
    debug('inSessionStats', 'placement calculated', {
      sessionId,
      liveEventQuizRankByPlayer,
      leaderboardSize: Object.keys(leaderboard).length,
      totalPlayers: summary.totalPlayers,
    });
    try {
      const { mstLiveLog } = await import('./mstLiveDebug');
      mstLiveLog('PLACEMENT', 'Final placement calculated', {
        eventId: sessionId,
        totalPlayers: summary.totalPlayers,
        top3Count: liveEventQuizRankByPlayer
          ? Object.values(liveEventQuizRankByPlayer).filter((r) => Number(r) <= 3).length
          : 0,
      });
    } catch {
      /* ignore */
    }

    // Store summary in session document. Optional summary fields are often undefined, which Firestore rejects.
    try {
      await updateDoc(sessionRef, {
        sessionSummary: stripUndefinedDeep(summary),
        status: 'ended',
        endedAt: sessionEndTime
      });
    } catch (summaryErr) {
      debugError('inSessionStats', 'Failed to save sessionSummary on room (continuing to archive)', summaryErr);
    }
    debug('inSessionStats', 'final score saved', {
      sessionId,
      totalPlayers: summary.totalPlayers,
      statsCount: Object.keys(summary.stats).length,
      hasQuizPp: !!summary.quizPpByPlayer,
      hasPowerGains: !!summary.liveEventPowerGains,
    });
    debug('inSessionStats', 'Live Event ended', {
      sessionId,
      status: 'ended',
      totalPlayers: summary.totalPlayers,
    });

    // Permanent Admin history archive (non-fatal — never block session end)
    try {
      const { archiveLiveEventSession } = await import('./liveEventHistoryService');
      await archiveLiveEventSession(sessionId, summary, sessionData);
      debug('inSessionStats', 'Live Event history archived', { sessionId });
    } catch (histErr) {
      debugError('inSessionStats', 'Live Event history archive failed (non-fatal)', histErr);
    }

    if (liveEventQuizRankByPlayer && Object.keys(liveEventQuizRankByPlayer).length > 0) {
      try {
        await setDoc(
          doc(db, 'liveEventPlacementRollups', sessionId),
          {
            sessionId,
            classId: sessionData.classId ?? '',
            rankByPlayer: liveEventQuizRankByPlayer,
            endedAt: sessionEndTime,
          },
          { merge: true }
        );
      } catch (rollupErr) {
        debugError('inSessionStats', 'Failed to write liveEventPlacementRollups', rollupErr);
      }

      const top3Entries = Object.entries(liveEventQuizRankByPlayer).filter(
        ([, rank]) => Number(rank) <= 3
      );
      for (const [playerId, rank] of top3Entries) {
        // Host finalization can't always write into other users' daily challenge subcollections.
        // Queue pending placement on stats so each player self-claims challenge credit.
        await setDoc(
          doc(db, 'inSessionRooms', sessionId, 'stats', playerId),
          {
            sessionEndWinChallengePlacementPending: Number(rank),
          },
          { merge: true }
        );
        debug('inSessionStats', 'Top 3 win credit awarded', {
          sessionId,
          playerId,
          placement: Number(rank),
          queuedForSelfClaim: true,
        });
      }
    }
    
    debug('inSessionStats', `Finalized session stats for ${sessionId}`, {
      totalPlayers: playerIds.length,
      mvp: mvpPlayerId
    });
    
    return summary;
  } catch (error) {
    debugError('inSessionStats', `Error finalizing session stats`, error);
    return null;
  }
}

/**
 * Apply session-end PP owed to this player's account (students / users / vault).
 * Host finalization stores the amount on `stats/{playerId}.sessionEndAccountPpPending` because only the signed-in
 * player may update their own `students`/`users` docs under current Firestore rules.
 */
/**
 * Apply session-end Power stat XP + matching Battle Pass XP from `stats/{playerId}` pending fields.
 * Idempotent: repeats no-op after `sessionEndPowerBpClaimedAt` is set.
 */
export async function claimLiveEventSessionEndPowerAndBattlePass(
  sessionId: string,
  playerId: string
): Promise<boolean> {
  const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
  type Reserved = { bp: number; gain: LiveEventPowerGain };
  let reserved: Reserved | null = null;
  try {
    reserved = await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(statsRef);
      if (!snap.exists()) return null;
      const d = snap.data() as SessionStats;
      if (d.sessionEndPowerBpClaimedAt) return null;
      const bp = Math.max(0, Math.floor(Number(d.sessionEndBattlePassXpPending) || 0));
      const gain = (d.sessionEndPowerGainPending || {}) as LiveEventPowerGain;
      if (bp <= 0 && !liveEventPowerGainHasPositiveAmount(gain)) return null;
      transaction.update(statsRef, {
        sessionEndBattlePassXpPending: deleteField(),
        sessionEndPowerGainPending: deleteField(),
        sessionEndPowerBpClaimedAt: serverTimestamp(),
      });
      return { bp, gain };
    });
  } catch (e) {
    debugError(
      'inSessionStats',
      `claimLiveEventSessionEndPowerAndBattlePass transaction failed for ${playerId}`,
      e
    );
    return false;
  }

  if (!reserved) return false;

  try {
    await applySessionEndBattlePassAndPowerToStudent(playerId, reserved.bp, reserved.gain);
    debug('inSessionStats', `Claimed session-end power/BP for ${playerId}`, reserved);
    return true;
  } catch (e) {
    debugError('inSessionStats', `claimLiveEventSessionEndPowerAndBattlePass apply failed for ${playerId}`, e);
    try {
      await updateDoc(statsRef, {
        ...(reserved.bp > 0 ? { sessionEndBattlePassXpPending: reserved.bp } : {}),
        ...(liveEventPowerGainHasPositiveAmount(reserved.gain)
          ? { sessionEndPowerGainPending: reserved.gain }
          : {}),
        sessionEndPowerBpClaimedAt: deleteField(),
      });
    } catch (restoreErr) {
      debugError(
        'inSessionStats',
        `Failed to restore pending power/BP after apply error for ${playerId}`,
        restoreErr
      );
    }
    return false;
  }
}

export async function claimLiveEventSessionEndPendingPp(sessionId: string, playerId: string): Promise<boolean> {
  const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
  let reserved = 0;
  try {
    reserved = await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(statsRef);
      if (!snap.exists()) return 0;
      const d = snap.data() as SessionStats;
      if (d.sessionEndAccountPpClaimedAt) return 0;
      const p = typeof d.sessionEndAccountPpPending === 'number' ? d.sessionEndAccountPpPending : 0;
      if (p <= 0) return 0;
      transaction.update(statsRef, {
        sessionEndAccountPpPending: deleteField(),
        sessionEndAccountPpClaimedAt: serverTimestamp(),
      });
      return p;
    });
  } catch (e) {
    debugError('inSessionStats', `claimLiveEventSessionEndPendingPp transaction failed for ${playerId}`, e);
    return false;
  }

  if (reserved <= 0) return false;

  try {
    await creditPPToStudentUserVault(playerId, reserved, 'Live Event session rewards');
    void trackPlayerAction(playerId, 'EARN_PP', reserved).catch((err) =>
      console.error('[inSessionStats] earn_pp after live event session-end claim:', err)
    );
    debug('inSessionStats', `Claimed ${reserved} session-end live event PP for ${playerId}`);
    return true;
  } catch (e) {
    debugError('inSessionStats', `claimLiveEventSessionEndPendingPp credit failed for ${playerId}`, e);
    try {
      await updateDoc(statsRef, {
        sessionEndAccountPpPending: reserved,
        sessionEndAccountPpClaimedAt: deleteField(),
      });
    } catch (restoreErr) {
      debugError('inSessionStats', `Failed to restore pending PP after credit error for ${playerId}`, restoreErr);
    }
    return false;
  }
}

export async function claimLiveEventSessionEndWinChallenge(
  sessionId: string,
  playerId: string
): Promise<boolean> {
  const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
  let reservedPlacement = 0;
  try {
    reservedPlacement = await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(statsRef);
      if (!snap.exists()) return 0;
      const d = snap.data() as SessionStats;
      if (d.sessionEndWinChallengeClaimedAt) return 0;
      const placement = Math.max(
        0,
        Math.floor(Number(d.sessionEndWinChallengePlacementPending) || 0)
      );
      if (placement <= 0 || placement > 3) return 0;
      transaction.update(statsRef, {
        sessionEndWinChallengePlacementPending: deleteField(),
        sessionEndWinChallengeClaimedAt: serverTimestamp(),
      });
      return placement;
    });
  } catch (e) {
    debugError('inSessionStats', `claimLiveEventSessionEndWinChallenge transaction failed for ${playerId}`, e);
    return false;
  }

  if (reservedPlacement <= 0) return false;

  try {
    const claim = await trackDailyChallengeProgress({
      userId: playerId,
      eventType: 'live_event',
      actionType: 'final_placement',
      challengeTypes: ['win_battle'],
      amount: 1,
      sourceId: `live-event-top3:${sessionId}:${playerId}`,
      liveEventId: sessionId,
      placement: reservedPlacement,
      metadata: {
        claimSource: 'sessionEndWinChallengePlacementPending',
      },
    });
    debug('inSessionStats', 'Top 3 win credit awarded', {
      sessionId,
      playerId,
      placement: reservedPlacement,
      duplicateSkipped: claim.duplicate === true,
      claimOk: claim.ok,
    });
    return claim.ok;
  } catch (e) {
    debugError('inSessionStats', `claimLiveEventSessionEndWinChallenge apply failed for ${playerId}`, e);
    try {
      await updateDoc(statsRef, {
        sessionEndWinChallengePlacementPending: reservedPlacement,
        sessionEndWinChallengeClaimedAt: deleteField(),
      });
    } catch (restoreErr) {
      debugError('inSessionStats', `Failed to restore pending win challenge after claim error for ${playerId}`, restoreErr);
    }
    return false;
  }
}

/**
 * Self-claim participate_live_event daily challenge credit queued at session finalize.
 * Idempotent via sessionEndParticipateChallengeClaimedAt + dailyChallengeEvents sourceId.
 */
export async function claimLiveEventSessionEndParticipateChallenge(
  sessionId: string,
  playerId: string
): Promise<boolean> {
  const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
  let reserved = false;
  try {
    reserved = await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(statsRef);
      if (!snap.exists()) return false;
      const d = snap.data() as SessionStats;
      if (d.sessionEndParticipateChallengeClaimedAt) return false;
      if (!d.sessionEndParticipateChallengePending) return false;
      transaction.update(statsRef, {
        sessionEndParticipateChallengePending: deleteField(),
        sessionEndParticipateChallengeClaimedAt: serverTimestamp(),
      });
      return true;
    });
  } catch (e) {
    debugError(
      'inSessionStats',
      `claimLiveEventSessionEndParticipateChallenge transaction failed for ${playerId}`,
      e
    );
    return false;
  }

  if (!reserved) return false;

  try {
    await trackPlayerAction(playerId, 'LIVE_EVENT_SESSION_FINALIZED', 1);
    debug('inSessionStats', `Claimed participate_live_event for ${playerId} session ${sessionId}`);
    return true;
  } catch (e) {
    debugError('inSessionStats', `claimLiveEventSessionEndParticipateChallenge apply failed for ${playerId}`, e);
    try {
      await updateDoc(statsRef, {
        sessionEndParticipateChallengePending: true,
        sessionEndParticipateChallengeClaimedAt: deleteField(),
      });
    } catch (restoreErr) {
      debugError(
        'inSessionStats',
        `Failed to restore participate challenge pending for ${playerId}`,
        restoreErr
      );
    }
    return false;
  }
}

/**
 * Run every session-end self-claim for this player (PP, Power/BP, win, participate).
 * Safe to call repeatedly — each claim is idempotent.
 * Home reclaim index is only marked settled when no account PP / Power / BP remains owed.
 */
export async function claimAllLiveEventSessionEndRewards(
  sessionId: string,
  playerId: string
): Promise<void> {
  await claimLiveEventSessionEndPendingPp(sessionId, playerId);
  await claimLiveEventSessionEndPowerAndBattlePass(sessionId, playerId);
  await claimLiveEventSessionEndWinChallenge(sessionId, playerId);
  await claimLiveEventSessionEndParticipateChallenge(sessionId, playerId);
  let classFlowPayoutsOwed = false;
  try {
    const { claimPendingClassFlowPayouts } = await import('./classFlowWorkService');
    classFlowPayoutsOwed = await claimPendingClassFlowPayouts(sessionId, playerId);
  } catch {
    classFlowPayoutsOwed = true;
  }
  try {
    const stillOwed = classFlowPayoutsOwed || (await sessionEndRewardsStillPending(sessionId, playerId));
    if (stillOwed) {
      debug(
        'inSessionStats',
        `Leaving liveEventPendingClaims pending — rewards still owed for ${playerId} in ${sessionId}`
      );
      return;
    }
    const { markLiveEventPendingClaimSettled } = await import('./liveEventPendingClaimsService');
    await markLiveEventPendingClaimSettled(sessionId, playerId);
  } catch {
    /* non-fatal */
  }
}

/** True if account PP or Power/BP session-end fields still need a successful claim. */
async function sessionEndRewardsStillPending(
  sessionId: string,
  playerId: string
): Promise<boolean> {
  try {
    const snap = await getDoc(doc(db, 'inSessionRooms', sessionId, 'stats', playerId));
    if (!snap.exists()) return false;
    const d = snap.data() as SessionStats;
    const ppPending =
      !d.sessionEndAccountPpClaimedAt &&
      typeof d.sessionEndAccountPpPending === 'number' &&
      d.sessionEndAccountPpPending > 0;
    const bpPending = Math.max(0, Math.floor(Number(d.sessionEndBattlePassXpPending) || 0)) > 0;
    const powerPending = liveEventPowerGainHasPositiveAmount(
      (d.sessionEndPowerGainPending || {}) as LiveEventPowerGain
    );
    const powerBpStillOwed = !d.sessionEndPowerBpClaimedAt && (bpPending || powerPending);
    return ppPending || powerBpStillOwed;
  } catch {
    // If we cannot read stats, keep the Home reclaim index open.
    return true;
  }
}

/**
 * Get session stats for a player
 */
export async function getPlayerStats(
  sessionId: string,
  playerId: string
): Promise<SessionStats | null> {
  try {
    const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerId);
    const statsDoc = await getDoc(statsRef);
    
    if (!statsDoc.exists()) {
      return null;
    }
    
    return statsDoc.data() as SessionStats;
  } catch (error) {
    debugError('inSessionStats', `Error getting player stats`, error);
    return null;
  }
}

/**
 * Get session summary
 */
export async function getSessionSummary(
  sessionId: string
): Promise<SessionSummary | null> {
  try {
    const sessionRef = doc(db, 'inSessionRooms', sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (!sessionDoc.exists()) {
      return null;
    }
    
    const sessionData = sessionDoc.data();
    return sessionData.sessionSummary || null;
  } catch (error) {
    debugError('inSessionStats', `Error getting session summary`, error);
    return null;
  }
}



